import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Code2, ShieldCheck, Radar, Scale, ChevronDown, Users } from "lucide-react";
import { useLanguage } from "@/i18n";
import Reveal, { MaskedLines } from "@/components/Reveal";
import CyberCanvas from "@/components/CyberCanvas";
import { Slider } from "@/components/ui/slider";

const SAAS_IMG = "https://images.unsplash.com/photo-1753998941540-081eed4f6397?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxjeWJlcnNlY3VyaXR5JTIwY29kZSUyMGRhcmslMjBzY3JlZW4lMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc5MzYyOTQ5MXww&ixlib=rb-4.1.0&q=85";
const AUDIT_IMG = "https://images.unsplash.com/photo-1760548425425-e42e77fa38f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxjeWJlcnNlY3VyaXR5JTIwY29kZSUyMGRhcmslMjBzY3JlZW4lMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc5MzYyOTQ5MXww&ixlib=rb-4.1.0&q=85";

const SERVICE_ICONS = [Code2, ShieldCheck, Users, Radar, Scale];
const BASE = [15000, 25000, 45000, 30000];
const PER_UNIT = [1200, 900, 3000, 800];
const WEEKS_BASE = [3, 4, 8, 6];
const CRIT_MULT = [1, 1.4, 1.9];

const chf = (n) => `${Math.round(n / 500) * 500}`.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
const slug = (n) => n.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "");

const Select = ({ testid, label, value, onChange, options }) => (
  <div>
    <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{label}</label>
    <div className="relative">
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full appearance-none rounded-xl border border-white/15 bg-[#0A0D14] px-4 py-3 pr-10 text-sm text-white outline-none transition-colors focus:border-cyan-400"
      >
        {options.map((o, i) => (
          <option key={o} value={i}>{o}</option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
    </div>
  </div>
);

const ITEstimator = () => {
  const { t } = useLanguage();
  const e = t.it.estimator;
  const [service, setService] = useState(0);
  const [scope, setScope] = useState(5);
  const [crit, setCrit] = useState(1);

  const { low, high, weeks } = useMemo(() => {
    const raw = (BASE[service] + PER_UNIT[service] * scope) * CRIT_MULT[crit];
    const w = Math.max(1, Math.round(WEEKS_BASE[service] + scope * 0.4 + (crit === 2 ? 2 : 0)));
    return { low: raw * 0.85, high: raw * 1.2, weeks: w };
  }, [service, scope, crit]);

  return (
    <section data-testid="calculator-it-container" className="px-4 py-24 sm:px-6">
      <div className="mx-auto max-w-5xl">
        <Reveal>
          <div className="rounded-3xl border border-cyan-400/20 bg-[#121824] p-6 sm:p-10">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400">{e.title}</p>
            <p className="mt-2 max-w-lg text-sm text-slate-400">{e.desc}</p>
            <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
              <div className="space-y-6">
                <Select testid="estimator-service-select" label={e.serviceLabel} value={service} onChange={setService} options={e.services} />
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{e.scopeLabel}</label>
                    <span className="font-mono text-sm font-bold text-cyan-300" data-testid="estimator-scope-value">{scope} {e.unit}</span>
                  </div>
                  <Slider
                    data-testid="estimator-scope-slider"
                    min={1}
                    max={50}
                    step={1}
                    value={[scope]}
                    onValueChange={([v]) => setScope(v)}
                  />
                </div>
                <Select testid="estimator-crit-select" label={e.critLabel} value={crit} onChange={setCrit} options={e.crit} />
              </div>
              <div className="flex flex-col justify-center rounded-2xl border border-cyan-400/25 bg-[#0A0D14] p-8 text-center">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">{e.result}</p>
                <p data-testid="estimator-result" className="mt-3 text-3xl font-extrabold text-cyan-300 sm:text-4xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  CHF {chf(low)} – {chf(high)}
                </p>
                <p data-testid="estimator-weeks" className="mt-2 font-mono text-sm text-slate-400">≈ {weeks} {e.weeks}</p>
                <p className="mt-4 text-xs text-slate-500">{e.note}</p>
                <Link
                  to="/contact"
                  data-testid="estimator-cta-btn"
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 px-6 py-3 text-sm font-bold text-slate-950 transition-colors duration-200 hover:bg-cyan-300"
                >
                  {e.cta}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const ITPage = () => {
  const { t } = useLanguage();
  const span = ["col-span-12 md:col-span-8", "col-span-12 md:col-span-4", "col-span-12 md:col-span-4", "col-span-12 md:col-span-4", "col-span-12 md:col-span-4"];
  const imgs = [SAAS_IMG, AUDIT_IMG, null, null, null];

  return (
    <main data-testid="section-pole-it" className="bg-[#0A0D14]">
      <section className="relative flex min-h-[92svh] items-end overflow-hidden px-4 pb-16 pt-32 sm:px-6">
        <img src={SAAS_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/80 to-[#0A0D14]/40" />
        <div className="absolute inset-0 bg-grid-dark" />
        <CyberCanvas className="hidden md:block" />
        <div className="relative z-10 mx-auto w-full max-w-7xl">
          <MaskedLines baseDelay={0.1} lines={[t.it.badge]} lineClassName="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400" />
          <MaskedLines
            baseDelay={0.25}
            lines={[t.it.hero1, t.it.hero2]}
            className="mt-5"
            lineClassName="text-4xl font-extrabold uppercase leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl"
          />
          <Reveal delay={0.7}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400">{t.it.heroSub}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#estimator" data-testid="it-hero-cta-btn" className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3.5 text-sm font-bold text-slate-950 transition-colors duration-200 hover:bg-cyan-300">
                {t.it.heroCta}
                <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#services" data-testid="it-hero-secondary-btn" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:border-cyan-400 hover:text-cyan-300">
                {t.it.heroCtaSecondary}
              </a>
            </div>
            <div className="mt-12 flex flex-wrap gap-8 border-t border-white/10 pt-8">
              {t.it.stats.map((s) => (
                <div key={s.l}>
                  <p className="text-2xl font-extrabold text-cyan-300 sm:text-3xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{s.v}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section id="services" className="px-4 py-24 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <h2 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {t.it.servicesTitle}
            </h2>
            <p className="mt-3 text-sm text-slate-400 sm:text-base">{t.it.servicesSub}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12">
            {t.it.services.map((s, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <Reveal key={s.t} delay={i * 0.08} className={span[i]}>
                  <div className="group relative h-full overflow-hidden rounded-3xl border border-white/10 bg-[#121824] p-7 transition-colors duration-300 hover:border-cyan-400/40 sm:p-9">
                    {imgs[i] && (
                      <>
                        <img src={imgs[i]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-10 transition-opacity duration-500 group-hover:opacity-20" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#121824] via-[#121824]/80 to-transparent" />
                      </>
                    )}
                    <div className="relative">
                      <Icon className="h-9 w-9 text-cyan-400" strokeWidth={1.5} />
                      <h3 className="mt-5 text-xl font-bold text-white sm:text-2xl">{s.t}</h3>
                      <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-400">{s.d}</p>
                      <div className="mt-5 flex flex-wrap gap-2">
                        {s.tags.map((tag) => (
                          <span key={tag} className="rounded-full border border-cyan-400/25 bg-cyan-400/5 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-cyan-300/90">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 pb-8 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-white/10 bg-[#070A10]">
              <div className="flex items-center gap-2 border-b border-white/10 px-5 py-3.5">
                <span className="h-3 w-3 rounded-full bg-red-500/70" />
                <span className="h-3 w-3 rounded-full bg-yellow-500/70" />
                <span className="h-3 w-3 rounded-full bg-emerald-500/70" />
                <span className="ml-3 font-mono text-xs text-slate-500">itranspro-soc — zsh</span>
              </div>
              <div className="p-6 font-mono text-sm leading-loose" data-testid="terminal-block">
                {t.it.terminal.map((line, i) => (
                  <p key={i} className={line.startsWith("$") ? "text-white" : line.includes("Vuln") || line.includes("Schwach") ? "text-red-400" : "text-emerald-400"}>
                    {line}
                  </p>
                ))}
                <p className="text-white">$ <span className="animate-cursor text-cyan-400">▌</span></p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-4 pb-8 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400">{t.it.refTitle}</p>
            <p className="mt-3 max-w-lg text-sm text-slate-400">{t.it.refSub}</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {t.it.refs.map((ref, i) => (
              <Reveal key={ref.name} delay={i * 0.08}>
                <a
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`it-ref-${slug(ref.name)}`}
                  className="group flex h-full flex-col justify-between rounded-3xl border border-white/10 bg-[#121824] p-7 transition-colors duration-300 hover:border-cyan-400/40"
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <h3 className="font-mono text-lg font-bold text-cyan-300">{ref.name}</h3>
                      <ArrowUpRight className="h-5 w-5 text-slate-500 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-cyan-300" />
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-slate-400">{ref.d}</p>
                  </div>
                  <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 transition-colors group-hover:text-cyan-400">
                    {t.it.refVisit} →
                  </p>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <div id="estimator" className="scroll-mt-20">
        <ITEstimator />
      </div>

      <section className="px-4 pb-24 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-[#121824] to-[#0A0D14] p-10 text-center sm:p-16">
              <h2 className="text-2xl font-extrabold tracking-tight text-white sm:text-4xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {t.it.ctaTitle}
              </h2>
              <p className="mx-auto mt-4 max-w-md text-sm text-slate-400 sm:text-base">{t.it.ctaSub}</p>
              <Link
                to="/contact"
                data-testid="it-cta-btn"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-8 py-4 text-sm font-bold text-slate-950 transition-colors duration-200 hover:bg-cyan-300"
              >
                {t.it.ctaBtn}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default ITPage;
